// La ficha de un contratado: el panel derecho del chat. 04-11-2026 (propuesta 2, aprobada por Pau: «me mola que te cagas,
// súbelo todo»; diseño web/qa/auditoria-apple/propuesta-2/fuentes/ficha.md, maqueta panel-despues.png). Pau: «decirme de
// qué va la conversación: por qué está y para qué. Abro uno y no me acuerdo de para qué es ni de quién es». En 3 segundos:
// qué es, de quién, para qué, dónde va; y sus fotos y archivos a un clic. Una pregunta por bloque, en el orden del ojo:
//   A · Quién: su nombre corto (se cambia aquí, con el lápiz), sala, quién lo pidió y cuándo.
//   B · Para qué: una frase FIJA escrita al contratar (no cambia a cada turno) y, debajo, tus palabras tal cual.
//   C · Dónde va: el estado sale SIEMPRE del código (situación, cierre, despertador), nunca del resumen; debajo, lo hecho
//       y lo siguiente de la ficha del resumidor, si los hay.
//   D · Con Santi: sus contratos, si los tiene.
//   E · Fotos y archivos (Galeria.tsx, de otro dueño: aquí solo se coloca).
//   F · Pie: modelo y lo al día que está el resumen.
// Lo que el agente te pide NO va aquí: vive en un solo sitio, la línea de encima de la caja (LineaAhora), con su botón.
// Clase de fallo que mata (ficha.md §2.2): la ficha nunca dice «no hay» a partir de un dato que FALTA. Sin dato, el bloque
// no se pinta (antes: «Todavía nada» al lado de «Probado con 1.200 conversaciones»).
import { ChevronRight, ExternalLink, Handshake, Pencil } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatClockTime } from '../../../lib/time';
import { useOficinaStore } from '../store';
import { MODELOS } from '../compartido/modelos';
import { LIMITES, POTENCIA_EN_CRISTIANO, type ContratadoDTO, type ExpedienteDTO } from '../compartido/vocabulario';
import { Aviso, Boton, BotonIcono, Campo, IconoEncargo } from '../ui';
import { oficinaApi } from '../api';
import { mensajeError } from '../errores';
import { hace, lineaDeEstado, quien } from '../marco/estado';
import { bloqueDe, etiquetaSituacion, fraseAparcado, grupoDelContratado, pasoCierreEnFrase, problemaNecesitaPau, teToca, type Grupo } from '../marco/situacion';
import { bandejaDe, miBandeja, type Yo } from '../marco/bandejas';
import { usePelotasStore } from '../pelotas/store';
import { diasHasta, YO } from '../pelotas/tiempo';
import { FilaArchivos, Galeria, useArchivos } from './Galeria';
import type { EstadoAgente } from '../marco/acciones';
import { useAgente } from '../marco/useAgente';

export type Necesidad = { texto: string; tono: 'pide' | 'nada' | 'problema' | 'terminado'; desde: string | null };

/**
 * Lo que necesita de Pau, de lo pendiente de verdad: para la línea de «qué pasa ahora» encima de la caja (ahoraMismo).
 * SI necesita algo lo decide LA clasificación de la lista (`grupo`, de marco/situacion.ts `grupoDelContratado` con su
 * contexto: Santi, su subida, la hora), nunca la situación a secas: antes un agente terminado o aparcado decía «Te
 * necesita» en su chat mientras la lista lo ponía en «Han terminado» o «Aparcados» (05-11, auditoría final flujo.md #3).
 * Solo cuando el estado no dice el qué («espera tu respuesta» a secas), el qué lo pone la ficha si es más nueva que su
 * último mensaje. Sin `grupo`, el que sale del DTO solo (sin Santi ni subida).
 */
export function necesitaDeTi(c: ContratadoDTO, ficha: ExpedienteDTO['ficha'] = null, grupo: Grupo = grupoDelContratado(c)): Necesidad {
  const desde = c.teNecesitaDesde ?? null;
  if (grupo === 'terminado') return { texto: fraseTerminado(c, ficha), tono: 'terminado', desde };
  if (grupo === 'problema_pau') return { texto: c.pide.trim(), tono: 'problema', desde };
  if (bloqueDe(grupo) === 'te_toca') {
    if (c.pide.trim()) return { texto: c.pide.trim(), tono: 'pide', desde };
    if (c.visual === 'te_necesita') return { texto: lineaDeEstado(c).texto, tono: 'pide', desde };
    const fresca = ficha?.ahora && ficha.en >= (c.ultimoMensaje?.en ?? '') ? ficha.ahora : '';
    // Una tarjeta para Santi esperando tu clic (grupo «te ha escrito» sin que el agente pregunte nada): la situación no lo dice.
    return { texto: fresca || (teToca(c.situacion) ? etiquetaSituacion(c.situacion) : t('oficinaV2.info.ficha.tarjetaSanti')), tono: 'pide', desde };
  }
  if (grupo === 'aparcado') return { texto: t('oficinaV2.info.ficha.aparcado'), tono: 'nada', desde: null };
  if (grupo === 'con_santi') return { texto: t('oficinaV2.info.ficha.conSantiNada'), tono: 'nada', desde: null };
  if (c.situacion === 'problema') return { texto: t('oficinaV2.info.ficha.problema'), tono: 'nada', desde: null };
  if (c.situacion === 'cerrando') return { texto: t('oficinaV2.info.ficha.cerrando'), tono: 'nada', desde: null };
  return { texto: t('oficinaV2.info.ficha.noNecesita'), tono: 'nada', desde: null };
}

/** Lo que entrega quien ha terminado: lo más nuevo entre su ficha y su último mensaje (sin «Terminado:» delante). */
function fraseTerminado(c: ContratadoDTO, ficha: ExpedienteDTO['ficha']): string {
  const fresca = ficha?.ahora && ficha.en >= (c.ultimoMensaje?.en ?? '') ? ficha.ahora : '';
  return (fresca || c.ultimoMensaje?.texto.trim() || c.titular.trim()).replace(/^(terminado|finished|done)\b\s*[:.,-]?\s*/i, '');
}

export type Ahora = { tono: 'pide' | 'problema' | 'terminado' | 'trabajando' | 'parado' | 'oficina'; etiqueta: string; texto: string };

/**
 * 13-10 (Pau: «me dice "sin nada pendiente" pero no veo qué está haciendo; quiero que el chat me diga si está haciendo
 * algo o si necesita algo de mí»): la línea fija encima del compositor. Lo que necesita sale de lo pendiente de verdad
 * (con el mismo `grupo` que la lista); si trabaja o está parado, del estado real del agente; el detalle, de la ficha si es
 * más nueva que lo último que pasó.
 */
export function ahoraMismo(c: ContratadoDTO, ficha: ExpedienteDTO['ficha'], grupo: Grupo = grupoDelContratado(c)): Ahora {
  const n = necesitaDeTi(c, ficha, grupo);
  if (n.tono === 'pide') return { tono: 'pide', etiqueta: t('oficinaV2.chat.ahora.teNecesita'), texto: n.texto };
  if (n.tono === 'problema') return { tono: 'problema', etiqueta: t('oficinaV2.chat.ahora.problema'), texto: n.texto };
  if (n.tono === 'terminado') return { tono: 'terminado', etiqueta: t('oficinaV2.chat.ahora.terminado'), texto: n.texto };
  if (grupo === 'aparcado') return { tono: 'parado', etiqueta: t('oficinaV2.chat.ahora.aparcado'), texto: '' };
  if (c.situacion === 'problema') return { tono: 'problema', etiqueta: t('oficinaV2.chat.ahora.problema'), texto: lineaDeEstado(c).texto };
  if (c.situacion === 'cerrando') return { tono: 'oficina', etiqueta: t('oficinaV2.chat.ahora.cerrando'), texto: '' };
  if (c.situacion === 'llegando') return { tono: 'trabajando', etiqueta: t('oficinaV2.chat.ahora.empezando'), texto: '' };
  const ultimo = [c.ultimoMensaje?.en, c.ultimoInformeEn].filter((x): x is string => !!x).sort().at(-1) ?? '';
  const fresca = ficha?.ahora && ficha.en >= ultimo ? ficha.ahora : '';
  if (c.situacion === 'trabajando') return { tono: 'trabajando', etiqueta: t('oficinaV2.chat.ahora.trabajando'), texto: fresca || c.titular || ficha?.ahora || '' };
  // Con Santi y quieto: espera a Santi (si trabaja, lo de arriba dice en qué, que es más útil).
  if (grupo === 'con_santi') return { tono: 'parado', etiqueta: t('oficinaV2.chat.ahora.conSanti'), texto: '' };
  return { tono: 'parado', etiqueta: t('oficinaV2.chat.ahora.parado'), texto: fresca || t('oficinaV2.chat.ahora.paradoSinMas') };
}

// ─── Lo que dice cada bloque (puro, probado en Informacion.test.tsx) ───

/** A · quién lo pidió, mirado por quien mira: «lo pediste tú», «lo pidió Carla», la oficina sola o un contrato con Santi. */
export function quienLoPidio(c: Pick<ContratadoDTO, 'pedidoPor' | 'dueno'>, yo: Yo | null): string {
  if (c.pedidoPor === 'oficina') return t('oficinaV2.info.ficha.loPusoLaOficina');
  if (c.pedidoPor === 'santi') return t('oficinaV2.info.ficha.paraContratoSanti');
  return bandejaDe(c) === miBandeja(yo) ? t('oficinaV2.info.ficha.loPedisteTu') : t('oficinaV2.info.ficha.loPidio', { nombre: c.dueno?.nombre ?? '' });
}

const DIA_SEMANA = new Intl.DateTimeFormat('es-ES', { weekday: 'long' });
const DIA_CORTO = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });

/** A · cuándo: «hoy», «ayer», «el lunes» hasta 6 días; luego «mar 3 nov». */
export function cuandoLoPidio(iso: string, ahora = Date.now()): string {
  const d = diasHasta(iso, ahora);
  if (!Number.isFinite(d)) return '';
  if (d >= 0) return t('oficinaV2.info.ficha.hoy');
  if (d === -1) return t('oficinaV2.info.ficha.ayer');
  if (d >= -6) return t('oficinaV2.info.ficha.elDia', { dia: DIA_SEMANA.format(new Date(iso)) });
  return DIA_CORTO.format(new Date(iso)).replace(/,/g, '');
}

/** C · cuándo vuelve a mirar quien duerme hasta una hora: «a las 14:20», «mañana a las 18:05», «el jueves a las 9:00». */
export function vuelveAMirar(iso: string, ahora = Date.now()): string {
  const d = diasHasta(iso, ahora);
  const hora = formatClockTime(iso);
  if (d <= 0) return t('oficinaV2.info.ficha.vuelveHoy', { hora });
  if (d === 1) return t('oficinaV2.info.ficha.vuelveManana', { hora });
  return t('oficinaV2.info.ficha.vuelveDia', { dia: DIA_SEMANA.format(new Date(iso)), hora });
}

export type Punto = 'trabajando' | 'quieto' | 'cerrando' | 'aviso' | 'error';

/**
 * C · dónde va, SOLO del código (situación, paso del cierre, despertador), nunca del resumen. Si te necesita, el título es
 * neutro («Pide tu permiso»): el qué y el botón están en la línea de encima de la caja.
 */
export function dondeVa(c: ContratadoDTO, ahora = Date.now(), e: Pick<EstadoAgente, 'grupo' | 'aparcado'> | null = null): { punto: Punto; titulo: string; detalle: string } {
  const detalle = c.vuelveA ? vuelveAMirar(c.vuelveA, ahora) : '';
  // 05-11 — lo que la lista ya sabe (marco/acciones.ts `estadoDelAgente`) manda: el que ha terminado no «espera tu
  // respuesta»; el que duerme, duerme; el que espera a Santi, espera a Santi.
  if (e?.grupo === 'terminado') return { punto: 'quieto', titulo: t('oficinaV2.info.ficha.terminado'), detalle: '' };
  if (e?.aparcado) return { punto: 'quieto', titulo: fraseAparcado(e.aparcado, ahora), detalle: '' };
  // Con Santi y quieto (si trabaja o cierra, el estado de abajo dice más).
  if (e?.grupo === 'con_santi' && (c.situacion === 'sin_pendiente' || teToca(c.situacion))) return { punto: 'quieto', titulo: t('oficinaV2.info.ficha.conSanti'), detalle };
  switch (c.situacion) {
    case 'llegando':
      return { punto: 'trabajando', titulo: t('oficinaV2.info.ficha.empezando'), detalle: '' };
    case 'trabajando':
      return { punto: 'trabajando', titulo: (c.titular ?? '').trim() || etiquetaSituacion('trabajando'), detalle };
    case 'cerrando':
      return { punto: 'cerrando', titulo: c.pasoCierre ? pasoCierreEnFrase(c.pasoCierre, c.modoCierre) : etiquetaSituacion('cerrando'), detalle: '' };
    case 'problema':
      return problemaNecesitaPau(c)
        ? { punto: 'error', titulo: etiquetaSituacion('problema'), detalle: '' }
        : { punto: 'aviso', titulo: t('oficinaV2.info.ficha.arreglando'), detalle: '' };
    case 'sin_pendiente':
      return { punto: 'quieto', titulo: (c.titular ?? '').trim() || etiquetaSituacion('sin_pendiente'), detalle };
    default:
      return { punto: 'quieto', titulo: etiquetaSituacion(c.situacion), detalle };
  }
}

const PUNTO: Record<Punto, string> = {
  trabajando: 'bg-oficina-trabajando',
  quieto: 'bg-oficina-texto3',
  cerrando: 'bg-oficina-cerrando',
  aviso: 'bg-oficina-bloqueado',
  error: 'bg-oficina-error',
};

const TARJETA = 'rounded-o-l bg-oficina-elevada px-4 py-3.5';

export function Informacion({ contratado: c, sala }: { contratado: ContratadoDTO; sala: string }) {
  const exp = useOficinaStore((s) => s.expedientes[c.agentId]);
  const cargar = useOficinaStore((s) => s.cargarExpediente);
  useEffect(() => {
    void cargar(c.agentId);
  }, [cargar, c.agentId]);
  // 13-10 — galería: se recarga cuando hay mensajes nuevos (de él o tuyos).
  const archivos = useArchivos(c.agentId, `${c.ultimoMensaje?.en ?? ''}|${exp?.ficha?.en ?? ''}`);
  const [galeria, setGaleria] = useState(false);
  const scroll = useRef<HTMLElement>(null);
  // Al cambiar de agente el panel se queda abierto y vuelve arriba (ficha.md §3, comportamiento).
  useEffect(() => {
    setGaleria(false);
    if (scroll.current) scroll.current.scrollTop = 0;
  }, [c.agentId]);

  if (galeria && archivos) {
    return (
      <aside className="h-full bg-oficina-panel" aria-label={t('oficinaV2.galeria.titulo')}>
        <Galeria agentId={c.agentId} archivos={archivos} nombreAgente={quien(c)} alVolver={() => setGaleria(false)} />
      </aside>
    );
  }

  return (
    <aside ref={scroll} className="flex h-full flex-col gap-3 overflow-y-auto bg-oficina-panel px-4 pb-4 pt-5" aria-label={t('oficinaV2.info.titulo')}>
      <Quien c={c} sala={sala} />
      <ParaQue c={c} exp={exp} />
      <DondeVa c={c} exp={exp} />
      <ConSanti agentId={c.agentId} />
      {archivos ? (
        <section data-testid="ficha-archivos" className={TARJETA}>
          {archivos.length ? (
            <FilaArchivos agentId={c.agentId} archivos={archivos} alVerTodo={() => setGaleria(true)} />
          ) : (
            <>
              <h3 className="text-o-sub font-medium text-oficina-texto2">{t('oficinaV2.galeria.titulo')}</h3>
              <p className="mt-1 text-o-sub text-oficina-texto3">{t('oficinaV2.info.ficha.archivosVacio')}</p>
            </>
          )}
        </section>
      ) : null}
      <Pie c={c} ficha={exp?.ficha ?? null} />
    </aside>
  );
}

/** A · Quién: el monigote, su nombre corto (con el lápiz) y «● Venuz · lo pediste tú · mar 3 nov». */
function Quien({ c, sala }: { c: ContratadoDTO; sala: string }) {
  const color = useOficinaStore((s) => s.salas.find((x) => x.projectId === c.projectId)?.color);
  const yo = useOficinaStore((s) => s.yo);
  return (
    <header data-testid="ficha-quien" className="flex items-center gap-3 px-1 pb-1">
      <IconoEncargo icono={c.icono} semilla={c.semillaAvatar} tamano={60} />
      <div className="min-w-0 flex-1">
        <Nombre contratado={c} />
        <p className="flex min-w-0 items-center gap-1.5 text-o-sub text-oficina-texto2">
          {color ? <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden /> : null}
          <span className="min-w-0 truncate">{[sala, quienLoPidio(c, yo), cuandoLoPidio(c.creadoEn)].filter(Boolean).join(' · ')}</span>
        </p>
      </div>
    </header>
  );
}

/**
 * B · Para qué: la frase fija de la recepción (o, de los de antes, su frase larga) y tus palabras en 2 líneas que se
 * despliegan. Sin ninguna de las dos, no se pinta.
 */
function ParaQue({ c, exp }: { c: ContratadoDTO; exp: ExpedienteDTO | undefined }) {
  const [entero, setEntero] = useState(false);
  useEffect(() => {
    setEntero(false);
  }, [c.agentId]);
  const frase = c.paraQue ?? (c.titulo && c.titulo !== c.tema ? c.titulo : null);
  const palabras = (exp?.mision ?? '').trim();
  if (!frase && !palabras) return null;
  return (
    <section data-testid="ficha-para-que" className={TARJETA}>
      <h3 className="text-o-sub font-medium text-oficina-texto2">{t('oficinaV2.info.ficha.paraQue')}</h3>
      {frase ? <p className="mt-1 text-o-cuerpo font-semibold leading-snug text-oficina-texto">{frase}</p> : null}
      {palabras ? (
        <button
          type="button"
          onClick={() => setEntero(!entero)}
          aria-expanded={entero}
          className={cn(
            'mt-1.5 block w-full rounded-o-s text-left text-o-sub text-oficina-texto3 outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
            entero ? 'whitespace-pre-wrap break-words' : 'line-clamp-2',
          )}
        >
          <span className="font-semibold text-oficina-texto2">{t('oficinaV2.info.ficha.tusPalabras')} </span>
          {t('oficinaV2.info.ficha.entreComillas', { texto: palabras })}
        </button>
      ) : null}
    </section>
  );
}

/** C · Dónde va: el estado del código con su punto; debajo, lo hecho y lo siguiente si la ficha los tiene. */
function DondeVa({ c, exp }: { c: ContratadoDTO; exp: ExpedienteDTO | undefined }) {
  const tarde = useRetraso(200, c.agentId);
  const agente = useAgente(c);
  if (!exp) {
    // Cargando: el esqueleto de la tarjeta (sin «Cargando…»), solo si tarda (ficha.md §3, estados).
    return tarde ? (
      <section data-testid="ficha-cargando" aria-hidden className={cn(TARJETA, 'space-y-2.5')}>
        <div className="h-3 w-1/3 rounded-full bg-oficina-control" />
        <div className="h-3 w-4/5 rounded-full bg-oficina-control" />
        <div className="h-3 w-2/3 rounded-full bg-oficina-control" />
      </section>
    ) : null;
  }
  const cerrado = exp.cierre.ciclo === 'cerrado' || exp.cierre.ciclo === 'despedido';
  if (cerrado) {
    return exp.cierre.informe ? (
      <section data-testid="ficha-donde" className={TARJETA}>
        <h3 className="text-o-sub font-medium text-oficina-texto2">{t('oficinaV2.info.ficha.resultado')}</h3>
        <Informe exp={exp} />
      </section>
    ) : null;
  }
  const d = dondeVa(c, agente.ahora, agente);
  const hecho = exp.ficha?.hecho.filter((h) => h.trim()).slice(0, 3) ?? [];
  const despues = (exp.ficha?.despues || exp.siguientePaso).trim();
  const nota = exp.cierre.informe?.modo === 'integrar' ? exp.cierre.informe : null;
  return (
    <section data-testid="ficha-donde" className={TARJETA}>
      <h3 className="text-o-sub font-medium text-oficina-texto2">{t('oficinaV2.info.ficha.dondeVa')}</h3>
      <p className="mt-1 flex items-start gap-2 text-o-cuerpo font-semibold leading-snug text-oficina-texto">
        <span data-testid="ficha-punto" data-punto={d.punto} aria-hidden className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', PUNTO[d.punto], d.punto === 'trabajando' && 'motion-safe:animate-pulse')} />
        <span className="min-w-0">{d.titulo}</span>
      </p>
      {d.detalle ? <p className="mt-0.5 pl-4 text-o-sub text-oficina-texto2">{d.detalle}</p> : null}
      {hecho.length ? (
        <ul className="mt-3 space-y-1 border-t border-oficina-linea pt-3 text-o-texto text-oficina-texto">
          {hecho.map((h, i) => (
            <li key={i} className="flex gap-2">
              {/* Un punto, no un ✓: es lo que el agente cuenta, resumido; la oficina no lo ha comprobado. */}
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-oficina-texto3" aria-hidden />
              <span className="min-w-0">{h}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {despues ? (
        <p className={cn('text-o-texto text-oficina-texto2', hecho.length ? 'mt-2' : 'mt-3 border-t border-oficina-linea pt-3')}>
          <span className="font-semibold text-oficina-texto">{t('oficinaV2.info.ficha.despues')} </span>
          {despues}
        </p>
      ) : null}
      {nota ? (
        <div className="mt-3 border-t border-oficina-linea pt-3">
          <h4 className="text-o-sub font-medium text-oficina-texto2">{t('oficinaV2.info.notaEntrega')}</h4>
          <Informe exp={exp} />
        </div>
      ) : null}
    </section>
  );
}

/** Lo que dejó escrito al integrar o al irse: el resultado, lo no hecho, si alguien sigue, el gasto y la propuesta. */
function Informe({ exp }: { exp: ExpedienteDTO }) {
  const { informe, integracion, enlace } = exp.cierre;
  if (!informe) return null;
  return (
    <div className="mt-1 space-y-1.5 text-o-texto text-oficina-texto2">
      <p className="text-o-cuerpo text-oficina-texto">{informe.resultado}</p>
      {integracion === 'integrada' ? <p className="text-oficina-trabajando">{t('oficinaV2.info.integrado')}</p> : null}
      {integracion === 'sin_integrar' ? <p>{t('oficinaV2.info.sinIntegrar')}</p> : null}
      {informe.noHecho.map((n, i) => (
        <Dato key={i} titulo={t('oficinaV2.info.noHecho')}>
          {t('oficinaV2.info.noHechoLinea', { que: n.que, por_que: n.por_que })}
        </Dato>
      ))}
      {informe.siContinuas ? <Dato titulo={t('oficinaV2.info.siContinuas')}>{informe.siContinuas}</Dato> : null}
      {informe.gasto ? (
        <Dato titulo={t('oficinaV2.info.gasto')}>
          {informe.gasto.enQue
            ? t('oficinaV2.info.gastoLinea', { dolares: informe.gasto.dolares.toFixed(2), en_que: informe.gasto.enQue })
            : t('oficinaV2.info.gastoSolo', { dolares: informe.gasto.dolares.toFixed(2) })}
        </Dato>
      ) : null}
      {enlace ? (
        <a href={enlace} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-oficina-enlace hover:underline">
          {t('oficinaV2.info.verPropuesta')}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      ) : null}
    </div>
  );
}

function Dato({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <p>
      <span className="text-oficina-texto3">{titulo} </span>
      {children}
    </p>
  );
}

/** D · Con Santi: el primer contrato de este agente (y «y 2 más»); abre la ventana Santi en él. Sin contratos, nada. */
function ConSanti({ agentId }: { agentId: string }) {
  const lista = usePelotasStore((s) => s.lista);
  const suyos = useMemo(() => (lista?.configurado ? lista.contratos.filter((x) => x.agentId === agentId) : []), [lista, agentId]);
  const primero = suyos[0];
  if (!primero) return null;
  const linea = t(primero.lado === YO ? 'oficinaV2.info.ficha.teToca' : 'oficinaV2.info.ficha.leToca', { hace: hace(primero.desde) });
  return (
    <button
      type="button"
      data-testid="ficha-santi"
      onClick={() => usePelotasStore.getState().abrirSanti(primero.temaId)}
      className={cn(TARJETA, 'flex w-full items-center gap-3 text-left outline-none transition-colors duration-150 hover:bg-oficina-control focus-visible:ring-2 focus-visible:ring-oficina-enlace/70')}
    >
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-o-s bg-oficina-santi/15 text-oficina-santi" aria-hidden>
        <Handshake className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-o-cuerpo font-semibold text-oficina-texto">{primero.titulo}</span>
        <span className="block truncate text-o-sub text-oficina-texto2">
          {linea}
          {suyos.length > 1 ? ` · ${t('oficinaV2.info.ficha.yMas', { n: suyos.length - 1 })}` : ''}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
    </button>
  );
}

/** F · Pie: «Opus 5.5 · potencia alta» ··· «Al día · hace 12 min» (o «Resumen de hace 40 min» si hay algo más nuevo). */
function Pie({ c, ficha }: { c: ContratadoDTO; ficha: ExpedienteDTO['ficha'] }) {
  // Con la ficha abierta, «hace 3 min» se recalcula solo cada minuto.
  const [, setTic] = useState(0);
  useEffect(() => {
    const reloj = setInterval(() => setTic((n) => n + 1), 60_000);
    return () => clearInterval(reloj);
  }, []);
  const modelo = MODELOS.find((m) => m.id === c.modelo)?.nombre ?? t('oficinaV2.desconocido');
  const alDia = !!ficha && ficha.en >= (c.ultimoMensaje?.en ?? '');
  return (
    <footer data-testid="ficha-pie" className="mt-auto flex items-center justify-between gap-3 px-1 pt-1 text-o-nota text-oficina-texto3">
      <span className="min-w-0 truncate">
        {modelo} · {t('oficinaV2.info.ficha.potencia', { potencia: POTENCIA_EN_CRISTIANO[c.potencia]?.toLowerCase() ?? '' })}
      </span>
      {ficha ? <span className="shrink-0">{t(alDia ? 'oficinaV2.info.ficha.alDia' : 'oficinaV2.info.ficha.resumenDe', { hace: hace(ficha.en) })}</span> : null}
    </footer>
  );
}

/** true pasado `ms` desde que cambió `clave` (el esqueleto solo aparece si la carga tarda). */
function useRetraso(ms: number, clave: string): boolean {
  const [tarde, setTarde] = useState(false);
  useEffect(() => {
    setTarde(false);
    const reloj = setTimeout(() => setTarde(true), ms);
    return () => clearTimeout(reloj);
  }, [ms, clave]);
  return tarde;
}

/**
 * El nombre, con un lápiz para cambiarlo (también se llega desde «Cambiar el nombre» del menú ⋮ del chat:
 * `renombrarPedido` del store). Enter guarda, Escape deja el que había; al salir, el foco vuelve al lápiz.
 * 04-11 — es el nombre corto («PRECIOS»): grande; los de antes, que son una frase, un punto más pequeño.
 */
function Nombre({ contratado: c }: { contratado: ContratadoDTO }) {
  const actualizar = useOficinaStore((s) => s.actualizarContratado);
  const pedido = useOficinaStore((s) => s.renombrarPedido === c.agentId);
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lapiz = useRef<HTMLButtonElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  // Solo al cerrar con teclado físico vuelve el foco al lápiz: con el dedo (también la tecla «OK» del teclado
  // del iPhone, que llega como Enter) darle el foco abriría su globo de ayuda encima de la ficha y se quedaría.
  const conTeclado = useRef(false);
  const anotar = (teclado: boolean) => {
    conTeclado.current = teclado && !window.matchMedia('(pointer: coarse)').matches;
  };
  // Si cambias de agente con la ficha abierta, no se queda a medias con el nombre del anterior.
  useEffect(() => {
    setEditando(false);
  }, [c.agentId]);
  useEffect(() => {
    if (!pedido) return;
    useOficinaStore.getState().pedirRenombrar(null);
    abrir();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al llegar el pedido
  }, [pedido]);
  useEffect(() => {
    if (editando) {
      campo.current?.focus();
      campo.current?.select();
    } else if (conTeclado.current) {
      conTeclado.current = false;
      lapiz.current?.focus();
    }
  }, [editando]);

  const limpio = valor.trim();
  const valido = limpio.length >= 3 && limpio.length <= LIMITES.tema;

  function abrir() {
    conTeclado.current = false;
    setValor(c.tema);
    setError(null);
    setEditando(true);
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    if (!valido || enviando) return;
    if (limpio === c.tema) return setEditando(false);
    setEnviando(true);
    setError(null);
    try {
      const r = await oficinaApi.renombrar(c.agentId, limpio);
      // El de ahora (el socket pudo traer otro estado mientras se escribía), no la copia de cuando se abrió.
      const ahora = useOficinaStore.getState().contratados[c.agentId];
      if (ahora) actualizar({ ...ahora, tema: r.tema });
      setEditando(false);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setEnviando(false);
    }
  }

  if (!editando) {
    const nombre = quien(c);
    return (
      <div className="flex max-w-full items-center gap-0.5">
        <h2 className={cn('min-w-0 break-words font-semibold leading-tight text-oficina-texto', nombre.length <= 18 ? 'text-o-titulo2' : 'text-o-titulo3')}>{nombre}</h2>
        <BotonIcono ref={lapiz} icono={Pencil} etiqueta={t('oficinaV2.info.renombrar')} tamano="sm" className="text-oficina-texto3" onClick={abrir} />
      </div>
    );
  }
  return (
    <form className="w-full space-y-2" onSubmit={(e) => void guardar(e)}>
      <Campo
        ref={campo}
        value={valor}
        maxLength={LIMITES.tema}
        aria-label={t('oficinaV2.info.nombre')}
        aria-describedby={valido ? undefined : `pista-nombre-${c.agentId}`}
        enterKeyHint="done"
        autoComplete="off"
        autoCapitalize="characters"
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Escape') anotar(true);
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setEditando(false);
          }
        }}
      />
      {valido ? null : (
        <p id={`pista-nombre-${c.agentId}`} className="text-o-sub text-oficina-texto3">
          {t('oficinaV2.info.nombreLargo', { min: 3, max: LIMITES.tema })}
        </p>
      )}
      {error ? <Aviso tono="error">{error}</Aviso> : null}
      <div className="flex gap-2">
        <Boton
          tamano="sm"
          onClick={(e) => {
            anotar(e.detail === 0); // 0 = pulsado con Intro/espacio, no con el dedo ni el ratón
            setEditando(false);
          }}
        >
          {t('oficinaV2.accion.cancelar')}
        </Boton>
        <Boton tamano="sm" variante="primario" type="submit" disabled={!valido} cargando={enviando} onClick={(e) => anotar(e.detail === 0)}>{t('oficinaV2.info.guardarNombre')}</Boton>
      </div>
    </form>
  );
}
